import axios from 'axios';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Read token from storage if set (fallback for cross-origin)
const storedToken = typeof window !== 'undefined' ? window.localStorage.getItem('amn_token') : null;

export const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json', ...(storedToken ? { Authorization: `Bearer ${storedToken}` } : {}) },
});

// Intercepteur 401 → nettoie le token expiré et redirige vers /login
// (sauf si on est déjà sur /login ou si l'erreur vient de l'endpoint /auth/me utilisé au démarrage)
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || '';
    if (status === 401 && !url.includes('/auth/me') && !url.includes('/auth/login')) {
      window.localStorage.removeItem('amn_token');
      delete api.defaults.headers.common.Authorization;
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login?expired=1';
      }
    }
    return Promise.reject(err);
  },
);

// Persist token when login sets it
const origPost = api.post.bind(api);
api.post = async (...args) => {
  const res = await origPost(...args);
  if (args[0] === '/auth/login' && res.data?.access_token) {
    window.localStorage.setItem('amn_token', res.data.access_token);
    api.defaults.headers.common.Authorization = `Bearer ${res.data.access_token}`;
  }
  if (args[0] === '/auth/logout') {
    window.localStorage.removeItem('amn_token');
    delete api.defaults.headers.common.Authorization;
  }
  return res;
};

export const fmtEUR = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Number(n || 0));
export const fmtPct = (n, d = 2) => `${Number(n || 0).toFixed(d)}%`;
export const fmtNum = (n, d = 0) => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: d, minimumFractionDigits: d }).format(Number(n || 0));

export const STATUT_LABELS = {
  faisa_cours: 'Faisabilité en cours',
  faisa_ko: 'Faisabilité KO',
  faisa_accord_probable: 'Faisabilité – Accord probable',
  projet_cours: 'Projet en cours de conception',
  pc_depose: 'Permis déposé',
  pc_accorde: 'Permis accordé',
};

export const STATUT_COLORS = {
  faisa_cours: 'bg-amber-50 text-amber-800 border-amber-300',
  faisa_ko: 'bg-rose-50 text-rose-700 border-rose-300',
  faisa_accord_probable: 'bg-emerald-50 text-emerald-800 border-emerald-300',
  projet_cours: 'bg-blue-50 text-[#002FA7] border-[#002FA7]',
  pc_depose: 'bg-indigo-50 text-indigo-800 border-indigo-400',
  pc_accorde: 'bg-violet-50 text-violet-800 border-violet-400',
};

export const PHASE_LABELS = {
  etude_faisabilite: 'Étude faisabilité',
  faisabilite_ok: 'Faisabilité OK',
  projet_conception: 'Projet en conception',
  pc_depose: 'Permis déposé',
  projet_accorde: 'Projet accordé',
  // legacy
  faisabilite: 'Étude faisabilité',
  projet: 'Projet en conception',
};

export const PHASES_ORDER = ['etude_faisabilite', 'faisabilite_ok', 'projet_conception', 'pc_depose', 'projet_accorde'];

export const PHASE_COLORS = {
  etude_faisabilite: '#dc2626',   // rouge — heures à risque
  faisabilite_ok: '#f59e0b',      // ambre — transition
  projet_conception: '#002FA7',   // bleu Klein — phase principale
  pc_depose: '#7c3aed',           // violet — dépôt
  projet_accorde: '#059669',      // vert — succès
};

export const PHASE_BG = {
  etude_faisabilite: 'bg-rose-50 text-rose-700 border-rose-300',
  faisabilite_ok: 'bg-amber-50 text-amber-700 border-amber-300',
  projet_conception: 'bg-blue-50 text-[#002FA7] border-[#002FA7]',
  pc_depose: 'bg-violet-50 text-violet-700 border-violet-400',
  projet_accorde: 'bg-emerald-50 text-emerald-700 border-emerald-400',
};

export const CATEGORIE_LABELS = {
  loyer: 'Loyer', salaires: 'Salaires', charges_sociales: 'Charges sociales',
  fournitures: 'Fournitures', logiciels: 'Logiciels', marketing: 'Marketing',
  impots: 'Impôts & taxes',
  deplacement: 'Déplacement', materiel: 'Matériel', sous_traitance: 'Sous-traitance',
  formation: 'Formation', exceptionnelle: 'Charge exceptionnelle',
  autre: 'Autre',
};

export const CATEGORIE_COLORS = {
  loyer: 'border-zinc-400 bg-zinc-50 text-zinc-700',
  salaires: 'border-blue-300 bg-blue-50 text-[#002FA7]',
  charges_sociales: 'border-indigo-300 bg-indigo-50 text-indigo-700',
  fournitures: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  logiciels: 'border-violet-300 bg-violet-50 text-violet-700',
  marketing: 'border-pink-300 bg-pink-50 text-pink-700',
  impots: 'border-rose-400 bg-rose-50 text-rose-700',
  deplacement: 'border-amber-300 bg-amber-50 text-amber-800',
  materiel: 'border-cyan-300 bg-cyan-50 text-cyan-700',
  sous_traitance: 'border-orange-300 bg-orange-50 text-orange-700',
  formation: 'border-teal-300 bg-teal-50 text-teal-700',
  exceptionnelle: 'border-red-400 bg-red-50 text-red-700',
  autre: 'border-zinc-300 bg-white text-zinc-600',
};

export const MODE_PAIEMENT_LABELS = {
  virement: 'Virement', cheque: 'Chèque', especes: 'Espèces',
  carte: 'Carte bancaire', prelevement: 'Prélèvement', autre: 'Autre',
};
